import { useMutation } from '@apollo/client';
import { Typography } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPDATE_USER } from '../graphql';
import SimpleInput from '../../common/inputs/simpleInput';
import OvForm from '../../common/wrappers/ovForm';

export interface UserState {
  uniqueCircumstances?: string,
  incompleteFields?: string[],
}

interface Props {
  user: UserState,
  updateUserState: (att: UserState) => void,
  title?: string,
  subtitle?: string,
  onContinue?: () => void,
}

const UniqueCircumstancesDescription = ({
  user, updateUserState, title, subtitle, ...props
}: Props): JSX.Element => {
  const { t } = useTranslation(['base', 'user']);
  const [loading, setLoading] = useState(false);

  const onEmployerNameUpdated = (data: { updateUser: { user: { incompleteFields: string[] } } }): void => {
    console.log({ event: 'EMPLOYER_NAME_UPDATED' });
    updateUserState({ ...user, incompleteFields: data.updateUser.user.incompleteFields });
    setLoading(false);
    if (props.onContinue) props.onContinue();
  };
  const [updateEmployerName] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        uniqueCircumstances: user.uniqueCircumstances?.trim(),
      },
    },
    onCompleted: onEmployerNameUpdated,
    onError: () => setLoading(false),
  });

  const onContinueClick = async (event: FormEvent<HTMLFormElement> | undefined): Promise<void> => {
    event?.preventDefault();
    setLoading(true);
    updateEmployerName().then();
  };

  const clearUniqueCircumstance = (): void => {
    updateUserState({ ...user, uniqueCircumstances: '' });
  };

  useEffect(() => {
    clearUniqueCircumstance();
    // eslint-disable-next-line
  }, []);

  return (
    <OvForm onSubmit={onContinueClick} loading={loading}>
      <Typography variant="heading2" style={{ marginBottom: 16 }}>{title ?? t('user:howToInvest.uniqueCircumstancesDescription.title')}</Typography>
      <Typography variant="paragraph3" style={{ marginBottom: 32 }}>{subtitle ?? t('user:howToInvest.uniqueCircumstancesDescription.subTitle')}</Typography>
      <SimpleInput
        testId="uniqueCircumstances-input"
        label={t('user:howToInvest.uniqueCircumstancesDescription.inputLabel')}
        value={user.uniqueCircumstances ?? ''}
        style={{ marginBottom: 16 }}
        required
        onChange={(event) => updateUserState({ ...user, uniqueCircumstances: event.target.value })}
        data-testid="uniqueCircumstances-input"
        onClear={clearUniqueCircumstance}
      />
    </OvForm>
  );
};

UniqueCircumstancesDescription.defaultProps = {
  title: undefined,
  subtitle: undefined,
  onContinue: undefined,
};

export default UniqueCircumstancesDescription;
